import React, { useMemo, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { DraggableItem, DraggableListSelector } from '@bringg-frontend/draggable-list-selector';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { BringgPopover } from '@bringg/react-components';

import useStore from '../../../stores/helpers/use-context';
import { TABLE_COLUMNS, TABLE_STATIC_COLUMNS } from '../table-columns';

const ColumnSelection: React.FC = () => {
	const { tableStore, translations, allConfiguration, customAttributes } = useStore();
	const { routesTableTranslations } = translations;
	const [isPopoverVisible, setIsPopoverVisible] = useState(false);

	const sortColumnsByPriority = (valuesObj: { [key: string]: DraggableItem }) => {
		return Object.entries(valuesObj)
			.sort(([keyA], [keyB]) => +keyA - +keyB)
			.map(values => values[1]);
	};

	const addCustomAttributes = (selectedValuesObj, unselectedValuesObj) => {
		customAttributes.forEach((attribute, i) => {
			if (tableStore.presentedColumns[attribute.id]) {
				const priority = tableStore.presentedColumns[attribute.id].priority;
				selectedValuesObj[priority] = {
					id: `${attribute.id}`,
					title: attribute.name
				};
			} else {
				const priority = Object.keys(TABLE_COLUMNS).length + Object.keys(TABLE_STATIC_COLUMNS).length + i + 1;
				unselectedValuesObj[priority] = {
					id: `${attribute.id}`,
					title: attribute.name
				};
			}
		});
	};

	const sortSelectedTableColumns = (selectedValuesObj, unselectedValuesObj) => {
		Object.entries(TABLE_COLUMNS).forEach(([columnKey, value]) => {
			const columnValues = tableStore.presentedColumns[columnKey];
			if (columnValues) {
				selectedValuesObj[columnValues.priority] = {
					id: value.id,
					title: routesTableTranslations.headers[value.title] || value.title
				};
			} else if (!value.condition || allConfiguration[value.condition]) {
				unselectedValuesObj[value.priority] = {
					id: value.id,
					title: routesTableTranslations.headers[value.title] || value.title
				};
			}
		});
	};

	const [selectedValues, unselectedValues] = useMemo(() => {
		const selectedValuesObj: { [key: string]: { id: string; title: string } } = {};
		const unselectedValuesObj = {};
		sortSelectedTableColumns(selectedValuesObj, unselectedValuesObj);

		const selectedColumns = {};
		Object.values(selectedValuesObj).forEach(value => (selectedColumns[value.id] = value));

		addCustomAttributes(selectedValuesObj, unselectedValuesObj);

		const sortedSelectedValues = sortColumnsByPriority(selectedValuesObj);
		const sortedUnselectedValues = sortColumnsByPriority(unselectedValuesObj);

		return [sortedSelectedValues, sortedUnselectedValues];
	}, [
		tableStore.presentedColumns,
		routesTableTranslations.headers,
		customAttributes,
		addCustomAttributes,
		sortSelectedTableColumns
	]);

	const onRestoreToDefault = () => {
		const defaultColumns = [];
		Object.values(TABLE_COLUMNS).forEach(column => {
			if (!column.condition || allConfiguration[column.condition]) {
				defaultColumns.push({ id: column.id, title: column.title });
			}
		});
		onApply(defaultColumns);

		const unselectedColumns = customAttributes.map(attribute => ({ id: `${attribute.id}`, title: attribute.name }));
		return { selectedValues: defaultColumns, unselectedValues: unselectedColumns };
	};

	const onApply = (items: DraggableItem[]) => {
		tableStore.setPresentedColumns(items);
		setIsPopoverVisible(false);
	};

	return (
		<BringgPopover
			visible={isPopoverVisible}
			onVisibleChange={setIsPopoverVisible}
			offset={[-50, 0]}
			destroyOnHide
			content={
				<DraggableListSelector
					translations={{
						title: routesTableTranslations.summaryDetails.columnsSelectionTitle,
						defaultButton: routesTableTranslations.summaryDetails.columnsSelectionDefault,
						selected: routesTableTranslations.summaryDetails.columnsSelectionSelected,
						nonSelected: routesTableTranslations.summaryDetails.columnsSelectionUnSelected,
						empty: routesTableTranslations.summaryDetails.columnsSelectionEmpty
					}}
					selectedValues={selectedValues}
					unselectedValues={unselectedValues}
					getDefault={onRestoreToDefault}
					onApply={onApply}
					onCancel={() => setIsPopoverVisible(false)}
				/>
			}
		>
			<div className="column-selection" data-test-id="column-selection-icon">
				<BringgIcon iconName={BringgFontIcons.Columns} />
			</div>
		</BringgPopover>
	);
};

export default observer(ColumnSelection);
